// src/components/pages/WorkoutPage.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useToast } from "../ui/use-toast";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  CardFooter // Keep CardFooter if needed elsewhere, otherwise remove
} from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Slider } from '../ui/slider'; // Keep Slider if needed elsewhere, otherwise remove
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { ScrollArea } from '../ui/scroll-area';
import { Switch } from '../ui/switch'; // Keep Switch if needed elsewhere, otherwise remove
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
  SelectLabel,
  SelectGroup
} from '../ui/select';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge'; // Keep Badge if needed elsewhere, otherwise remove
import { Separator } from '../ui/separator';
import { Textarea } from "../ui/textarea";
import { format } from 'date-fns';
import InitialWeightsDialog from '../workout/InitialWeightsDialog';
import ExerciseProgressChart from '../workout/ExerciseProgressChart';
import PersonalRecords from '../workout/PersonalRecords';
import CustomExercisesManager from '../workout/CustomExercisesManager';
import WorkoutTemplateManager from '../workout/WorkoutTemplateManager';
import OnboardingWizard from '../onboarding/OnboardingWizard';
// Add this import at the top with other imports:
import WorkoutProgramPreview from '../workout/WorkoutProgramPreview';
import {
  Dumbbell,
  Play,
  Pause, // Keep Pause if needed elsewhere, otherwise remove
  RotateCcw, // Keep RotateCcw if needed elsewhere, otherwise remove
  Plus,
  Minus,
  ChevronRight,
  Clock, // Keep Clock if needed elsewhere, otherwise remove
  Save,
  CheckCircle2,
  ArrowLeft,
  Settings,
  Calendar, // Keep Calendar if needed elsewhere, otherwise remove
  BarChart3, // Keep BarChart3 if needed elsewhere, otherwise remove
  Edit,
  Trash2,
  Check,
  Eye // Import Eye icon for template preview (although used in child component, needed if passing button JSX)
} from 'lucide-react';
import EnhancedRestTimer from '../workout/EnhancedRestTimer';
import DeleteWorkoutDialog from '../workout/DeleteWorkoutDialog';

// Default workout plans
const WORKOUT_TEMPLATES = {
  'stronglifts5x5': {
    templateKey: 'stronglifts5x5', // Add templateKey for preview identification
    name: '5x5 Strength',
    description: 'A simple, effective strength program focusing on the five most effective compound exercises',
    frequency: '3 days per week (A/B split)',
    level: 'Beginner',
    goal: 'Strength',
    workouts: {
      'a': {
        name: 'Workout A',
        exercises: [
          { name: 'Squat', sets: 5, reps: 5, repType: 'static', restTime: 90, increment: 5, type: 'barbell' },
          { name: 'Bench Press', sets: 5, reps: 5, repType: 'static', restTime: 90, increment: 5, type: 'barbell' },
          { name: 'Barbell Row', sets: 5, reps: 5, repType: 'static', restTime: 90, increment: 5, type: 'barbell' }
        ]
      },
      'b': {
        name: 'Workout B',
        exercises: [
          { name: 'Squat', sets: 5, reps: 5, repType: 'static', restTime: 90, increment: 5, type: 'barbell' },
          { name: 'Overhead Press', sets: 5, reps: 5, repType: 'static', restTime: 90, increment: 5, type: 'barbell' },
          { name: 'Deadlift', sets: 1, reps: 5, repType: 'static', restTime: 90, increment: 10, type: 'barbell' }
        ]
      }
    },
    progression: {
      failStrategy: 'deload',
      deloadPercentage: 10,
      initialWeights: {
        'Squat': 45,
        'Bench Press': 45,
        'Barbell Row': 65,
        'Overhead Press': 45,
        'Deadlift': 95
      }
    }
  },
  'fullBodyHypertrophy': {
    templateKey: 'fullBodyHypertrophy', // Add templateKey for preview identification
    name: '3-Day Full Body Hypertrophy',
    description: 'A balanced hypertrophy program targeting each muscle group 3x per week',
    frequency: '3 days per week (M/W/F)',
    level: 'Intermediate',
    goal: 'Muscle growth',
    workouts: {
      'day1': {
        name: 'Day 1',
        exercises: [
          { name: 'Back Squat', sets: 3, reps: 5, repType: 'static', restTime: 180, increment: 5, type: 'barbell' },
          { name: 'Bench Press', sets: 3, reps: 5, repType: 'static', restTime: 180, increment: 5, type: 'barbell' },
          { name: 'Barbell Row', sets: 3, repMin: 6, repMax: 10, repType: 'dynamic', restTime: 90, increment: 5, type: 'barbell' },
          { name: 'Romanian Deadlift', sets: 3, repMin: 8, repMax: 12, repType: 'dynamic', restTime: 90, increment: 5, type: 'barbell' },
          { name: 'Overhead Press', sets: 3, repMin: 6, repMax: 10, repType: 'dynamic', restTime: 90, increment: 5, type: 'barbell' },
          { name: 'Lat Pulldown', sets: 3, repMin: 8, repMax: 12, repType: 'dynamic', restTime: 90, increment: 5, type: 'cable' },
          { name: 'Bicep Curl', sets: 3, repMin: 10, repMax: 15, repType: 'dynamic', restTime: 60, increment: 2.5, type: 'dumbbell', optional: true }
        ]
      },
      'day2': {
        name: 'Day 2',
        exercises: [
          { name: 'Deadlift', sets: 2, reps: 5, repType: 'static', restTime: 240, increment: 10, type: 'barbell' },
          { name: 'Incline Dumbbell Press', sets: 3, repMin: 8, repMax: 12, repType: 'dynamic', restTime: 90, increment: 5, type: 'dumbbell' },
          { name: 'Leg Press', sets: 3, repMin: 10, repMax: 15, repType: 'dynamic', restTime: 120, increment: 10, type: 'machine' },
          { name: 'Seated Cable Row', sets: 3, repMin: 8, repMax: 12, repType: 'dynamic', restTime: 90, increment: 5, type: 'cable' },
          { name: 'Lateral Raise', sets: 3, repMin: 12, repMax: 15, repType: 'dynamic', restTime: 60, increment: 2.5, type: 'dumbbell' },
          { name: 'Hamstring Curl', sets: 3, repMin: 10, repMax: 15, repType: 'dynamic', restTime: 90, increment: 5, type: 'machine' },
          { name: 'Triceps Pushdown', sets: 3, repMin: 10, repMax: 15, repType: 'dynamic', restTime: 60, increment: 2.5, type: 'cable', optional: true }
        ]
      },
      'day3': {
        name: 'Day 3',
        exercises: [
          { name: 'Bench Press', sets: 3, reps: 5, repType: 'static', restTime: 180, increment: 5, type: 'barbell' },
          { name: 'Back Squat', sets: 3, reps: 5, repType: 'static', restTime: 180, increment: 5, type: 'barbell' },
          { name: 'Chest-Supported Row', sets: 3, repMin: 8, repMax: 12, repType: 'dynamic', restTime: 90, increment: 5, type: 'dumbbell' },
          { name: 'Overhead Press (Variation)', sets: 3, repMin: 6, repMax: 10, repType: 'dynamic', restTime: 90, increment: 5, type: 'dumbbell' },
          { name: 'Bulgarian Split Squat', sets: 3, repMin: 8, repMax: 12, repType: 'dynamic', restTime: 90, increment: 5, type: 'dumbbell' },
          { name: 'Face Pull', sets: 3, repMin: 15, repMax: 20, repType: 'dynamic', restTime: 60, increment: 2.5, type: 'cable' },
          { name: 'Calf Raise', sets: 3, repMin: 15, repMax: 20, repType: 'dynamic', restTime: 60, increment: 5, type: 'machine', optional: true }
        ]
      }
    },
    progression: {
      staticRepStrategy: 'fixedIncrement',
      dynamicRepStrategy: 'doubleProgression',
      deloadStrategy: { type: 'percentage', value: 10 },
      initialWeights: {
        'Back Squat': 135, 'Bench Press': 135, 'Deadlift': 185, 'Barbell Row': 95,
        'Overhead Press': 65, 'Romanian Deadlift': 135, 'Incline Dumbbell Press': 40,
        'Lat Pulldown': 100, 'Seated Cable Row': 100, 'Chest-Supported Row': 30,
        'Leg Press': 225, 'Bulgarian Split Squat': 20, 'Hamstring Curl': 70,
        'Face Pull': 40, 'Lateral Raise': 15, 'Bicep Curl': 20, 'Triceps Pushdown': 50,
        'Calf Raise': 100
      }
    }
  }
};

// Standard available plates (in lbs)
const DEFAULT_PLATES_LBS = [
  { weight: 45, count: 6 },
  { weight: 25, count: 6 },
  { weight: 10, count: 6 },
  { weight: 5, count: 6 },
  { weight: 2.5, count: 6 }
];

// Standard available plates (in kg)
const DEFAULT_PLATES_KG = [
  { weight: 20, count: 6 },
  { weight: 15, count: 2 },
  { weight: 10, count: 6 },
  { weight: 5, count: 6 },
  { weight: 2.5, count: 6 },
  { weight: 1.25, count: 6 }
];

// Default bar weights
const DEFAULT_BAR_WEIGHT = {
  imperial: 45, // lbs
  metric: 20 // kg
};

// Warmup calculation logic
const calculateWarmupSets = (workingWeight, exercise, unit) => {
  const barWeight = unit === 'imperial' ? DEFAULT_BAR_WEIGHT.imperial : DEFAULT_BAR_WEIGHT.metric;
   // Ensure workingWeight is a valid number
   const validWorkingWeight = parseFloat(workingWeight);
   if (isNaN(validWorkingWeight) || validWorkingWeight <= barWeight) {
     return [];
   }

  let warmupSets = [];
  warmupSets.push({ weight: barWeight, reps: 5, notes: 'Empty Bar' }); // Changed note slightly

  const weightDifference = validWorkingWeight - barWeight;
  let percentages = [];

  if (weightDifference < 50) {
    percentages = [0.8];
  } else if (weightDifference < 100) {
    percentages = [0.5, 0.8];
  } else {
    percentages = [0.4, 0.6, 0.8];
  }

  percentages.forEach((percentage, index) => {
    const warmupWeight = barWeight + (weightDifference * percentage);
    const roundingFactor = unit === 'imperial' ? 5 : 2.5;
    // Round to nearest factor, ensure not below bar weight
    const roundedWeight = Math.max(barWeight, Math.round(warmupWeight / roundingFactor) * roundingFactor);
    const reps = 5 - Math.min(index, 2); // 5, 4, 3 reps

    // Avoid adding duplicate weight sets if rounding makes them the same as previous or the bar
    if (roundedWeight > barWeight && (warmupSets.length === 1 || roundedWeight > warmupSets[warmupSets.length - 1].weight)) {
        warmupSets.push({
            weight: roundedWeight,
            reps,
            notes: `${Math.round(percentage * 100)}%`
        });
    }
  });

  return warmupSets;
};

// Calculate which plates to use
const calculatePlates = (weight, barWeight, availablePlates) => {
   // Ensure weight and barWeight are valid numbers
   const validWeight = parseFloat(weight);
   const validBarWeight = parseFloat(barWeight);
   if (isNaN(validWeight) || isNaN(validBarWeight)) return "Invalid Weight";

  const weightPerSide = (validWeight - validBarWeight) / 2;

  if (weightPerSide < -0.01) return "Error: Below Bar Weight"; // Allow for slight floating point inaccuracies
  if (Math.abs(weightPerSide) < 0.01) return "Empty Bar";

  // Ensure availablePlates is an array
  const platesArray = Array.isArray(availablePlates) ? availablePlates : [];

  const sortedPlates = [...platesArray]
    .filter(p => p && typeof p.weight === 'number' && p.weight > 0 && typeof p.count === 'number' && p.count > 0) // More robust filtering
    .sort((a, b) => b.weight - a.weight);

  let remainingWeight = weightPerSide;
  const platesUsed = [];
  const usedPlateCounts = {}; // Track how many of each plate type are used per side

  for (const plate of sortedPlates) {
    // Max pairs available for this plate type
    const maxPairs = Math.floor(plate.count / 2);
    if (maxPairs === 0) continue; // Skip if not enough for a pair

    // Calculate how many plates *per side* are needed
    const countNeededPerSide = Math.floor(remainingWeight / plate.weight);
    // Limit by available pairs
    const countUsedPerSide = Math.min(countNeededPerSide, maxPairs);

    if (countUsedPerSide > 0) {
      platesUsed.push({ weight: plate.weight, count: countUsedPerSide });
      remainingWeight -= (plate.weight * countUsedPerSide);
      if (remainingWeight < 0.01) { // Tolerance for floating point
          remainingWeight = 0;
          break;
      }
    }
  }

  const plateString = platesUsed
     .filter(p => p.count > 0) // Filter out plates with count 0 if any slipped through
     .map(p => `${p.count}×${p.weight}`)
     .join(", ");

  if (remainingWeight > 0.01) { // Check remaining with tolerance
    const remainingStr = remainingWeight.toFixed(1);
    return plateString ? `${plateString} + ${remainingStr} / side?` : `Cannot make ${weightPerSide.toFixed(1)} / side`;
  }

  return plateString ? `${plateString} / side` : "Empty Bar";
};


// Main Workout Page Component
const WorkoutPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('history');
  const [loading, setLoading] = useState(true); // Start true
  const [workoutHistory, setWorkoutHistory] = useState([]);
  const [activeWorkout, setActiveWorkout] = useState(null);
  const [showStartWorkoutDialog, setShowStartWorkoutDialog] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showWeightsDialog, setShowWeightsDialog] = useState(false);
  const [needsInitialSetup, setNeedsInitialSetup] = useState(false);
  const [templates, setTemplates] = useState([]);
  // State for workout-specific settings
  const [userSettings, setUserSettings] = useState({
    unit: 'imperial',
    plates: DEFAULT_PLATES_LBS,
    barWeight: DEFAULT_BAR_WEIGHT.imperial,
    activeTemplate: 'stronglifts5x5',
    lastWorkoutType: 'a',
    restTimes: { default: 90, exercises: {} },
    incrementSettings: {}
  });
  // State for general user preferences (gender, age, height, etc.)
  const [userPreferencesData, setUserPreferencesData] = useState(null);
  const [exerciseProgress, setExerciseProgress] = useState({});
  const [showWarmup, setShowWarmup] = useState(false);
  const [workoutToDelete, setWorkoutToDelete] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [recentHistory, setRecentHistory] = useState(null); // For onboarding prefill
  const [showWeightEditDialog, setShowWeightEditDialog] = useState(false);
  const [currentEditingExercise, setCurrentEditingExercise] = useState(null);
  const [showProgramPreview, setShowProgramPreview] = useState(false);
  const [previewProgramData, setPreviewProgramData] = useState(null);


  // Helper function to get rest time
  const getRestTimeForExercise = (exerciseName) => {
    return userSettings.restTimes?.exercises?.[exerciseName] ?? userSettings.restTimes?.default ?? 90; // Add fallbacks
  };

  // *** Consolidated useEffect for loading ALL user data ***
  useEffect(() => {
    const loadData = async () => {
       if (!user?.id) {
           setLoading(false);
           // Reset states for logged-out user
           setWorkoutHistory([]);
           setExerciseProgress({});
           setTemplates([]);
           setUserSettings({ // Reset workout settings
               unit: 'imperial', plates: DEFAULT_PLATES_LBS, barWeight: DEFAULT_BAR_WEIGHT.imperial,
               activeTemplate: 'stronglifts5x5', lastWorkoutType: 'a',
               restTimes: { default: 90, exercises: {} }, incrementSettings: {}
           });
           setUserPreferencesData(null); // Reset general preferences
           setNeedsInitialSetup(false);
           setRecentHistory(null);
           setShowOnboarding(false); // Ensure onboarding is closed
           setActiveWorkout(null); // Ensure no active workout
           setActiveTab('history'); // Reset to history tab
           return;
       }

      setLoading(true); // Indicate loading START
      let loadedPrefs = null;
      let loadedSettings = null;
      let formattedProgress = {};
      let historyData = [];
      let templatesData = [];

      try {
        // Fetch all data concurrently for efficiency
        const [prefsResult, settingsResult, progressResult, historyResult, templatesResult] = await Promise.all([
          supabase.from('user_preferences').select('*').eq('id', user.id).maybeSingle(), // maybeSingle handles no row found gracefully
          supabase.from('user_workout_settings').select('*').eq('id', user.id).maybeSingle(),
          supabase.from('exercise_progress').select('*').eq('user_id', user.id),
          supabase.from('workouts').select(`*, workout_exercises (*, workout_sets (*))`).eq('user_id', user.id).order('completed_at', { ascending: false }).limit(10),
          supabase.from('workout_templates').select('*').eq('user_id', user.id)
        ]);

        // --- Process Preferences ---
        if (prefsResult.error) throw new Error(`Preferences fetch failed: ${prefsResult.error.message}`);
        loadedPrefs = prefsResult.data;
        setUserPreferencesData(loadedPrefs); // Update state

        // --- Process Workout Settings ---
        if (settingsResult.error) throw new Error(`Workout settings fetch failed: ${settingsResult.error.message}`);
        loadedSettings = settingsResult.data;

        // Determine unit (Settings > Prefs > Default)
        const unit = loadedSettings?.unit || loadedPrefs?.unit || 'imperial';
        // Update userSettings state
        setUserSettings({
          unit: unit,
          activeTemplate: loadedSettings?.active_template || 'stronglifts5x5',
          lastWorkoutType: loadedSettings?.last_workout_type || 'a',
          plates: loadedSettings?.plates || (unit === 'metric' ? DEFAULT_PLATES_KG : DEFAULT_PLATES_LBS),
          barWeight: loadedSettings?.bar_weight || (unit === 'metric' ? DEFAULT_BAR_WEIGHT.metric : DEFAULT_BAR_WEIGHT.imperial),
          restTimes: loadedSettings?.rest_times || { default: 90, exercises: {} },
          incrementSettings: loadedSettings?.increment_settings || {}
        });

        // --- Process Progress ---
        if (progressResult.error) throw new Error(`Progress fetch failed: ${progressResult.error.message}`);
        formattedProgress = {};
        (progressResult.data || []).forEach(item => {
          formattedProgress[item.exercise_name] = {
            currentWeight: item.current_weight,
            lastCompletedReps: item.last_completed_reps,
            failCount: item.fail_count
          };
        });
        setExerciseProgress(formattedProgress);

        // --- Process History ---
        if (historyResult.error) throw new Error(`History fetch failed: ${historyResult.error.message}`);
        historyData = historyResult.data || [];
        setWorkoutHistory(historyData);

        // --- Process Templates ---
        if (templatesResult.error) throw new Error(`Templates fetch failed: ${templatesResult.error.message}`);
        templatesData = templatesResult.data || [];
        setTemplates(templatesData);

        // --- Determine if initial setup is needed ---
        const currentActiveTemplateKey = loadedSettings?.active_template || 'stronglifts5x5';
        let templateForCheck;
        if (currentActiveTemplateKey.startsWith('custom_')) {
            const templateId = currentActiveTemplateKey.replace('custom_', '');
            templateForCheck = templatesData?.find(t => t.id === templateId);
        } else {
            templateForCheck = WORKOUT_TEMPLATES[currentActiveTemplateKey];
        }

        let needsSetup = false;
        if (templateForCheck?.workouts) {
            const allExercises = new Set();
            Object.values(templateForCheck.workouts).forEach(w => (w.exercises || []).forEach(e => e.name && allExercises.add(e.name)));
            if (allExercises.size > 0) {
                const exercisesWithProgress = Object.keys(formattedProgress);
                needsSetup = Array.from(allExercises).some(exName =>
                    !exercisesWithProgress.includes(exName) &&
                    !exercisesWithProgress.some(progEx => progEx.includes(exName) || exName.includes(progEx))
                );
            }
        } else {
             console.warn("Active template or its workouts not found for initial weight check:", currentActiveTemplateKey);
             needsSetup = Object.keys(formattedProgress).length === 0; // Need setup if no progress and template missing/invalid
        }
        setNeedsInitialSetup(needsSetup);
        // --- End initial setup check ---

        // --- Onboarding Check (moved here to use loadedPrefs) ---
         if (loadedPrefs?.onboarding_complete !== true) {
             console.log("Onboarding incomplete detected during initial load.");
             // Fetch recent history ONLY if onboarding is needed
             const nutritionData = await fetchNutritionData(user.id);
             setRecentHistory(nutritionData);
             setShowOnboarding(true);
         } else {
             setShowOnboarding(false); // Ensure closed if complete
             setRecentHistory(null); // Clear history if not needed
         }
        // --- End Onboarding Check ---

      } catch (error) {
        console.error('Error loading user data:', error);
        toast({ title: 'Error Loading Data', description: error.message, variant: 'destructive' });
        // Reset states on error
        setWorkoutHistory([]); setExerciseProgress({}); setTemplates([]);
        setUserSettings({ unit: 'imperial', plates: DEFAULT_PLATES_LBS, barWeight: DEFAULT_BAR_WEIGHT.imperial, activeTemplate: 'stronglifts5x5', lastWorkoutType: 'a', restTimes: { default: 90, exercises: {} }, incrementSettings: {} });
        setUserPreferencesData(null);
        setNeedsInitialSetup(true); // Assume setup needed on error
        setRecentHistory(null); setShowOnboarding(false);
      } finally {
        setLoading(false); // Indicate loading END
      }
    }; // end loadData

    loadData(); // Execute the data loading function

  // Only re-run if the user object changes (login/logout) or toast function reference changes (unlikely)
  }, [user, toast]);


  // useEffect for initial weights setup prompt (runs *after* loading)
  useEffect(() => {
    // Conditions: Not loading, setup needed, user exists, weights dialog NOT open, onboarding NOT open
    if (!loading && needsInitialSetup && user?.id && !showWeightsDialog && !showOnboarding) {
        toast({
            title: "Starting Weights Needed",
            description: "Please set your starting weights for the active program.",
            duration: 10000,
            action: ( <Button onClick={() => setShowWeightsDialog(true)}>Set Weights</Button> )
        });
    }
  }, [needsInitialSetup, loading, showWeightsDialog, showOnboarding, toast, user]);


  // Function to fetch recent nutrition data (reusable)
  const fetchNutritionData = async (userId) => {
    if (!userId) return null;
    try {
      const { data, error } = await supabase
        .from('nutrition_history')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle(); // Use maybeSingle
      if (error) throw error; // Throw any actual error
      return data; // Returns data or null
    } catch (error) {
      console.error('Error fetching nutrition data:', error);
      toast({ title: 'Error', description: 'Could not fetch recent nutrition data.', variant: 'destructive' });
      return null;
    }
  };


  // Handler for weights updated from InitialWeightsDialog
  const handleWeightsUpdated = (updatedWeights) => {
    const newProgress = { ...exerciseProgress };
    Object.entries(updatedWeights).forEach(([exercise, weight]) => {
       const numWeight = parseFloat(weight);
       if (!isNaN(numWeight)) { // Only update if it's a valid number
           if (!newProgress[exercise]) {
               newProgress[exercise] = { currentWeight: numWeight, failCount: 0, lastCompletedReps: [] };
           } else {
               newProgress[exercise].currentWeight = numWeight;
           }
       }
    });
    setExerciseProgress(newProgress);
    setNeedsInitialSetup(false); // Assume setup is done after weights are set/updated
    toast({ title: "Weights Saved", description: "Starting weights updated." });
  };

  // Function to show program preview modal
  const handlePreviewProgram = (templateKey) => {
    let programData = null;
    if (!templateKey) {
        toast({ title: "Error", description: "No program selected.", variant: "destructive" });
        return;
    }
    if (templateKey.startsWith('custom_')) {
      const templateId = templateKey.replace('custom_', '');
      programData = templates?.find(t => t.id === templateId);
    } else {
      programData = WORKOUT_TEMPLATES[templateKey];
    }

    if (programData) {
      setPreviewProgramData(programData);
      setShowProgramPreview(true);
    } else {
      toast({ title: "Error", description: "Could not load program details.", variant: "destructive" });
    }
  };

  // Function to start a new workout session
  const handleStartWorkout = (workoutKey) => {
     if (!userSettings.activeTemplate) {
        toast({ title: "Error", description: "No active workout program selected.", variant: "destructive" }); return;
     }

    const isCustomTemplate = userSettings.activeTemplate.startsWith('custom_');
    let templateDefinition;
    let workoutDefinition;

    if (isCustomTemplate) {
        const templateId = userSettings.activeTemplate.replace('custom_', '');
        templateDefinition = templates.find(t => t.id === templateId);
    } else {
        templateDefinition = WORKOUT_TEMPLATES[userSettings.activeTemplate];
    }

    if (!templateDefinition) {
        toast({ title: "Error", description: "Selected program definition not found.", variant: "destructive" }); return;
    }
    workoutDefinition = templateDefinition.workouts?.[workoutKey];

    if (!workoutDefinition || !Array.isArray(workoutDefinition.exercises)) {
        toast({ title: "Error", description: `Workout day '${workoutKey}' not found or invalid for this program.`, variant: "destructive" }); return;
    }

    // Check for missing starting weights for *this specific workout's* exercises
    const missingWeights = workoutDefinition.exercises
      .map(exercise => {
        const progressWeight = exerciseProgress[exercise.name]?.currentWeight;
        const initialWeight = templateDefinition.progression?.initialWeights?.[exercise.name];
        // Needs setup if no progress AND no initial weight defined (or if progress is null/undefined)
         return (progressWeight === undefined || progressWeight === null) && (initialWeight === undefined || initialWeight === null) ? exercise.name : null;
      })
      .filter(name => name !== null); // Filter out nulls

    if (missingWeights.length > 0) {
        toast({ title: "Missing Starting Weights", description: `Set weights for: ${missingWeights.join(', ')}`, action: ( <Button onClick={() => setShowWeightsDialog(true)}>Set Weights</Button> ), variant: "destructive", duration: 10000 });
        setNeedsInitialSetup(true); // Ensure setup flag is true if weights are missing
        return;
    }
    setNeedsInitialSetup(false); // No missing weights for this workout, clear the flag

    // Initialize active workout state
    const exercises = workoutDefinition.exercises.map(exercise => {
        const restTime = getRestTimeForExercise(exercise.name);
        const progress = exerciseProgress[exercise.name];
        const initialWeight = templateDefinition.progression?.initialWeights?.[exercise.name];
        // Prioritize progress weight, then initial weight, then 0
        const weight = progress?.currentWeight ?? initialWeight ?? 0;
        const warmupSets = calculateWarmupSets(weight, exercise, userSettings.unit);
        const plateConfig = calculatePlates(weight, userSettings.barWeight, userSettings.plates);

        // Get exercise params safely, falling back to defaults
        const sets = parseInt(exercise.sets) || 3;
        const repType = exercise.repType || 'static';
        const reps = parseInt(exercise.reps) || 5; // Target for static
        const repMin = parseInt(exercise.repMin) || (repType === 'dynamic' ? 8 : reps); // Min for dynamic
        const repMax = parseInt(exercise.repMax) || (repType === 'dynamic' ? 12 : reps); // Max for dynamic
        // Determine default increment based on name or exercise data
        const defaultIncrement = exercise.increment || (exercise.name?.toLowerCase().includes('deadlift') ? 10 : 5);
        // Use custom increment setting if available, otherwise use default
        const increment = userSettings.incrementSettings?.[exercise.name] ?? defaultIncrement;


        return {
            ...exercise, // Base exercise data
            sets, repType, reps, repMin, repMax, increment, // Use determined/validated values
            weight, warmupSets, plateConfig, restTime,
            completedSets: new Array(sets).fill(0), // Array of 0s for completion status
            setStartTime: null // For potential set timer logic
        };
    });

    setActiveWorkout({
        type: workoutKey,
        name: workoutDefinition.name || `Workout ${workoutKey}`,
        exercises,
        startTime: new Date(),
        notes: '',
        templateSource: userSettings.activeTemplate // Store which template was used
    });

    setShowStartWorkoutDialog(false);
    setActiveTab('workout'); // Switch to workout tab
   };

  // Handle rest timer completion
  const handleTimerComplete = (exerciseId) => {
    toast({ title: 'Rest Complete!', description: 'Time for the next set.', duration: 5000 });
  };

  // Handle tapping a rep bubble
  const handleRepTap = (exerciseIndex, setIndex) => {
    setActiveWorkout(prev => {
      if (!prev) return null;
      const exercises = [...prev.exercises];
      const exercise = { ...exercises[exerciseIndex] }; // Shallow copy exercise
      const completedSets = [...exercise.completedSets]; // Shallow copy sets array

      const targetReps = parseInt(exercise.repType === 'static' ? exercise.reps : exercise.repMax, 10);
      const currentReps = completedSets[setIndex];

      if (currentReps === 0) { // First tap: Mark as complete with target reps
        completedSets[setIndex] = targetReps;
      } else { // Subsequent taps: Decrease by 1, minimum 0
        completedSets[setIndex] = Math.max(0, currentReps - 1);
      }

      exercise.completedSets = completedSets;
      exercises[exerciseIndex] = exercise;
      return { ...prev, exercises }; // Return updated workout state
    });
  };

  // Function to finish and save the active workout
  const handleFinishWorkout = async () => {
    if (!activeWorkout || !user?.id) return;
    setLoading(true); // Indicate saving START

    try {
        const hasCompletedSets = activeWorkout.exercises.some(ex => ex.completedSets.some(reps => reps > 0));
        if (!hasCompletedSets) {
            toast({ title: 'No Sets Logged', description: 'Log at least one rep before finishing.', variant: 'default' });
            setLoading(false); return; // Stop if nothing logged
        }

        // 1. Insert workout record
        const { data: workoutData, error: workoutError } = await supabase
          .from('workouts')
          .insert({ user_id: user.id, template: activeWorkout.templateSource, workout_type: activeWorkout.type, started_at: activeWorkout.startTime.toISOString(), completed_at: new Date().toISOString(), notes: activeWorkout.notes || '' })
          .select().single();
        if (workoutError) throw workoutError;
        const workoutId = workoutData.id;

        // 2. Prepare exercise, set, and progress data
        let exercisesToInsert = [];
        let setsToInsert = [];
        let progressToUpsert = [];

        activeWorkout.exercises.forEach((exercise, index) => {
            // Exercise record for this workout instance
            exercisesToInsert.push({ workout_id: workoutId, exercise_name: exercise.name, weight: exercise.weight, position: index });

            // Progression Logic - Calculate next weight and fail count
             let nextWeight = exercise.weight;
             let failCount = exerciseProgress[exercise.name]?.failCount || 0;
             const defaultIncrement = exercise.increment; // Already determined in handleStartWorkout

             if (exercise.repType === 'static') {
                 const requiredReps = exercise.reps;
                 const allSetsSuccessful = exercise.completedSets.every(reps => reps >= requiredReps);
                 if (allSetsSuccessful) {
                     nextWeight += defaultIncrement;
                     failCount = 0; // Reset on success
                 } else {
                     failCount++;
                     if (failCount >= 3) { // Deload after 3 failures
                         nextWeight = Math.max(userSettings.barWeight, Math.round((exercise.weight * 0.9) / 2.5) * 2.5); // 10% deload, round
                         failCount = 0; // Reset after deload
                     }
                     // Keep same weight if failCount < 3
                 }
             } else { // Dynamic reps (Double Progression)
                 const minReps = exercise.repMin;
                 const maxReps = exercise.repMax;
                 const hitMinRepsOnAll = exercise.completedSets.every(reps => reps >= minReps);
                 const hitMaxRepsOnAny = exercise.completedSets.some(reps => reps >= maxReps);

                 if (hitMinRepsOnAll && hitMaxRepsOnAny) { // Increment weight if min met on all AND max hit on at least one
                     nextWeight += defaultIncrement;
                     failCount = 0; // Reset on success
                 } else if (!hitMinRepsOnAll) { // If failed to meet min reps on any set
                     failCount++;
                     if (failCount >= 3) { // Deload after 3 failures
                         nextWeight = Math.max(userSettings.barWeight, Math.round((exercise.weight * 0.9) / 2.5) * 2.5); // 10% deload, round
                         failCount = 0; // Reset after deload
                     }
                     // Keep same weight if failCount < 3
                 } else { // Met min on all, but didn't hit max on any
                     failCount = 0; // Not a failure, just stay at current weight
                 }
             }

             // Ensure nextWeight is a valid number, fallback to bar weight if calculation fails
             nextWeight = isNaN(nextWeight) ? userSettings.barWeight : Math.max(userSettings.barWeight, nextWeight);

            progressToUpsert.push({
                user_id: user.id, exercise_name: exercise.name,
                current_weight: nextWeight,
                last_completed_reps: exercise.completedSets, // Store completed reps array
                fail_count: failCount, updated_at: new Date().toISOString()
            });
        });

        // 3. Batch insert exercises to get their IDs
        const { data: insertedExercises, error: exercisesError } = await supabase
            .from('workout_exercises').insert(exercisesToInsert).select();
        if (exercisesError) throw exercisesError;

        // 4. Map inserted exercise IDs and prepare sets data
        const exerciseIdMap = {};
        insertedExercises.forEach(exData => {
             // Map using name and position to ensure uniqueness if exercise appears multiple times
             exerciseIdMap[`${exData.exercise_name}-${exData.position}`] = exData.id;
        });

        activeWorkout.exercises.forEach((exercise, index) => {
            const workout_exercise_id = exerciseIdMap[`${exercise.name}-${index}`];
            if (workout_exercise_id) {
                exercise.completedSets.forEach((reps, setIndex) => {
                    const target_reps = exercise.repType === 'static' ? exercise.reps : exercise.repMax;
                    const target_reps_min = exercise.repType === 'dynamic' ? exercise.repMin : null;
                    setsToInsert.push({
                        workout_exercise_id, set_number: setIndex + 1, reps_completed: reps,
                        target_reps: target_reps,
                        ...(target_reps_min !== null && { target_reps_min: target_reps_min })
                    });
                });
            } else { console.warn(`Could not find workout_exercise_id for ${exercise.name} at pos ${index}`); }
        });

        // 5. Batch insert sets
        if (setsToInsert.length > 0) {
            const { error: setsError } = await supabase.from('workout_sets').insert(setsToInsert);
            if (setsError) throw setsError;
        }

        // 6. Batch upsert progress
        if (progressToUpsert.length > 0) {
            const { error: progressError } = await supabase.from('exercise_progress').upsert(progressToUpsert, { onConflict: 'user_id, exercise_name' });
            if (progressError) throw progressError;
        }

        // 7. Update user settings (last workout type if needed)
         let nextWorkoutType = userSettings.lastWorkoutType;
         let updateSettings = false;
         const currentTemplate = WORKOUT_TEMPLATES[activeWorkout.templateSource] || templates.find(t => `custom_${t.id}` === activeWorkout.templateSource);
         // Example: Simple A/B toggle for 5x5 or similar structures
         if (currentTemplate && Object.keys(currentTemplate.workouts || {}).length === 2 && ['a', 'b'].includes(activeWorkout.type)) {
             nextWorkoutType = activeWorkout.type === 'a' ? 'b' : 'a';
             updateSettings = true;
         }
         // Add logic for other rotation schemes if needed

         if (updateSettings && nextWorkoutType !== userSettings.lastWorkoutType) {
             const { error: settingsError } = await supabase.from('user_workout_settings').update({ last_workout_type: nextWorkoutType, updated_at: new Date().toISOString() }).eq('id', user.id);
             if (settingsError) console.error("Error updating last workout type:", settingsError); // Log but don't block
             setUserSettings(prev => ({ ...prev, lastWorkoutType: nextWorkoutType })); // Update local state immediately
         }

        // 8. Post-save UI updates
        toast({ title: 'Workout Completed!', description: 'Progress saved successfully.', duration: 4000 });
        setActiveWorkout(null); // Clear active workout

        // --- Refresh local state without full page reload ---
        // Re-fetch latest history
        const { data: historyData, error: historyError } = await supabase
            .from('workouts').select(`*, workout_exercises (*, workout_sets (*))`).eq('user_id', user.id).order('completed_at', { ascending: false }).limit(10);
        if (!historyError) setWorkoutHistory(historyData || []); else console.error("Error refreshing history:", historyError);

        // Update local exercise progress state directly from upserted data
        const refreshedProgress = { ...exerciseProgress };
        progressToUpsert.forEach(item => {
            refreshedProgress[item.exercise_name] = {
                currentWeight: item.current_weight, lastCompletedReps: item.last_completed_reps, failCount: item.fail_count
            };
        });
        setExerciseProgress(refreshedProgress);
        // --- End refresh ---

        setActiveTab('history'); // Switch back to history tab

      } catch (error) {
        console.error('Error saving workout:', error);
        toast({ title: 'Save Error', description: `Failed to save workout: ${error.message}`, variant: 'destructive', duration: 6000 });
      } finally {
        setLoading(false); // Indicate saving END
      }
   };

  // Handle initiating workout deletion
  const handleDeleteWorkout = async (workoutId) => {
    if (!workoutId || !user?.id) return;
    setIsDeleting(true);
    try {
        // CASCADE DELETE should handle related exercises/sets if configured in Supabase
        const { error } = await supabase
            .from('workouts').delete().match({ id: workoutId, user_id: user.id }); // Match both ID and user_id
        if (error) throw error;

        setWorkoutHistory(prev => prev.filter(workout => workout.id !== workoutId));
        toast({ title: 'Workout Deleted', description: 'Workout removed successfully.' });
        setShowDeleteDialog(false);
        setWorkoutToDelete(null);
      } catch (error) {
        console.error('Error deleting workout:', error);
        toast({ title: 'Delete Error', description: `Failed to delete workout: ${error.message}`, variant: 'destructive' });
      } finally {
        setIsDeleting(false);
      }
   };

   // Function to re-run onboarding wizard
   const resetOnboardingStatus = async () => {
     if (!user?.id) return;
     setLoading(true); // Indicate loading state

     try {
        // Fetch the latest nutrition data *before* resetting the flag & opening
        const nutritionData = await fetchNutritionData(user.id);
        setRecentHistory(nutritionData); // Store it for the wizard prefill

        // Update the onboarding flag in the database
        const { error } = await supabase
            .from('user_preferences')
            .update({ onboarding_complete: false, updated_at: new Date().toISOString() })
            .eq('id', user.id);

        if (error) {
            console.error('Error resetting onboarding status in DB:', error);
            toast({ title: "Error Updating Status", description: "Could not reset status. Opening wizard anyway.", variant: "destructive" });
        } else {
             // Update local preferences state if it exists
             setUserPreferencesData(prev => ({...(prev || {}), onboarding_complete: false}));
             toast({ title: "Setup Wizard Re-opened", description: "Update your profile settings." });
        }

        // Show the wizard regardless of DB update success/failure
        setShowOnboarding(true);

     } catch (error) {
         console.error('Unexpected error in resetOnboardingStatus:', error);
         toast({ title: "Error", description: "Could not re-open setup wizard.", variant: "destructive" });
     } finally {
         setLoading(false); // Stop loading indicator
     }
 };


  // Save active workout template preference
  const saveTemplatePreference = async (templateKey) => {
    if (!templateKey || userSettings.activeTemplate === templateKey || !user?.id) return;

    setLoading(true); // Indicate activity
    try {
        // Optimistically update local state for responsiveness
        const oldTemplate = userSettings.activeTemplate;
        setUserSettings(prev => ({ ...prev, activeTemplate: templateKey }));

        const { error } = await supabase
          .from('user_workout_settings')
          .update({ active_template: templateKey, updated_at: new Date().toISOString() })
          .eq('id', user.id);

        if (error) {
            setUserSettings(prev => ({ ...prev, activeTemplate: oldTemplate })); // Revert on error
            throw error;
        }

        // Re-evaluate if initial setup is needed for the NEW template
        let templateDefinition;
        if (templateKey.startsWith('custom_')) {
            templateDefinition = templates.find(t => `custom_${t.id}` === templateKey);
        } else {
            templateDefinition = WORKOUT_TEMPLATES[templateKey];
        }

        let needsSetup = false;
        if (templateDefinition?.workouts) {
            const allExercises = new Set();
            Object.values(templateDefinition.workouts).forEach(w => (w.exercises || []).forEach(e => e.name && allExercises.add(e.name)));
            if (allExercises.size > 0) {
                const exercisesWithProgress = Object.keys(exerciseProgress);
                needsSetup = Array.from(allExercises).some(exName =>
                     !exercisesWithProgress.includes(exName) &&
                     !exercisesWithProgress.some(progEx => progEx.includes(exName) || exName.includes(progEx))
                );
            }
        } else {
             needsSetup = Object.keys(exerciseProgress).length === 0;
        }
        setNeedsInitialSetup(needsSetup); // Update flag based on new template

        // Find template name for toast
        const templateName = templateDefinition?.name || "Selected Program";
        toast({ title: "Program Changed", description: `Active program set to ${templateName}.` });

      } catch (error) {
        console.error('Error saving template preference:', error);
        toast({ title: "Error", description: `Failed to update program: ${error.message}`, variant: "destructive" });
      } finally {
        setLoading(false);
      }
   };

     // Callback when a new custom template is added
  const handleTemplateAdded = (newTemplate) => {
    if (!newTemplate || !newTemplate.id) {
        console.error("WORKOUT_PAGE: Invalid new template received."); return;
    }
    // Add, sort, and update state
    setTemplates(prev => [...prev, newTemplate].sort((a, b) => a.name.localeCompare(b.name)) );
    // Optional: Automatically activate?
    // saveTemplatePreference(`custom_${newTemplate.id}`);
  };

     // Callback when a custom template is deleted
  const handleTemplateDeleted = (deletedTemplateId) => {
    setTemplates(prev => prev.filter(t => t.id !== deletedTemplateId));
    const deletedTemplateKey = `custom_${deletedTemplateId}`;
    // If the deleted template was active, reset to a default
    if (userSettings.activeTemplate === deletedTemplateKey) {
      const defaultTemplateKey = Object.keys(WORKOUT_TEMPLATES)[0] || '';
      saveTemplatePreference(defaultTemplateKey); // Save new default preference
      toast({ title: "Active Program Reset", description: "Program reset to default as the active one was deleted.", duration: 5000 });
    }
  };

  // ---- RENDER FUNCTIONS ----

  // Render Workout History List
  const renderWorkoutHistory = () => {
    if (loading && workoutHistory.length === 0) return <p className="text-center text-muted-foreground py-8">Loading history...</p>;
    if (!loading && workoutHistory.length === 0) {
      return (
        <div className="text-center py-8">
          <Dumbbell className="mx-auto h-12 w-12 text-muted-foreground opacity-50 mb-4" />
          <p className="text-muted-foreground mb-4">No workout history yet.</p>
           <Button onClick={() => setShowStartWorkoutDialog(true)} disabled={loading || needsInitialSetup}>
                <Play className="h-4 w-4 mr-2" /> Start First Workout
            </Button>
             {needsInitialSetup && !loading && <p className="text-xs text-destructive mt-2">Please set starting weights first (Settings tab).</p>}
        </div>
      );
    }

    const getWorkoutName = (templateKey, workoutTypeKey) => {
        let name = `Workout ${workoutTypeKey || 'Unknown'}`;
        try {
            let templateDefinition = null;
             if (templateKey?.startsWith('custom_')) {
                const tId = templateKey.replace('custom_', '');
                templateDefinition = templates.find(t => t.id === tId);
            } else if (templateKey && WORKOUT_TEMPLATES[templateKey]) {
                 templateDefinition = WORKOUT_TEMPLATES[templateKey];
            }
             name = templateDefinition?.workouts?.[workoutTypeKey]?.name || name;
             // Optionally add template name if workout name is generic
             if (name === `Workout ${workoutTypeKey || 'Unknown'}` && templateDefinition?.name) {
                  name = `${templateDefinition.name} - ${name}`;
             }
        } catch (e) { console.error("Error getting workout name:", e); }
        return name;
    };

    return (
      <div className="space-y-4">
        {workoutHistory.map((workout) => (
          <Card key={workout.id}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1">
                  <CardTitle className="text-lg leading-tight">
                      {getWorkoutName(workout.template, workout.workout_type)}
                  </CardTitle>
                  <CardDescription>
                     {workout.completed_at ? format(new Date(workout.completed_at), 'EEE, MMM d, yyyy') : 'Date unknown'}
                     {workout.completed_at && <span className="text-xs"> ({format(new Date(workout.completed_at), 'p')})</span>}
                  </CardDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={() => { setWorkoutToDelete(workout.id); setShowDeleteDialog(true); }} className="text-muted-foreground hover:text-destructive h-8 w-8 flex-shrink-0" title="Delete Workout" disabled={isDeleting}>
                  <Trash2 className="h-4 w-4" /> <span className="sr-only">Delete</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-2 pb-4">
              <div className="space-y-3">
                 {(workout.workout_exercises || []).sort((a,b) => (a.position ?? 0) - (b.position ?? 0)).map((exercise) => {
                    const totalSets = exercise.workout_sets?.length || 0;
                    let setDetails = "No sets recorded";
                    let targetRepsDisplay = "-";

                    if(totalSets > 0) {
                        const firstSet = exercise.workout_sets[0];
                        if (firstSet?.target_reps != null) {
                            targetRepsDisplay = firstSet.target_reps_min != null && firstSet.target_reps_min !== firstSet.target_reps
                                ? `${firstSet.target_reps_min}-${firstSet.target_reps}`
                                : firstSet.target_reps;
                        }
                        setDetails = exercise.workout_sets.map(s => s.reps_completed ?? 0).join(', ');
                    }

                  return (
                    <div key={exercise.id || exercise.exercise_name} className="text-sm border-b border-muted/30 pb-2 last:border-b-0 last:pb-0">
                      <div className="flex justify-between items-center font-medium">
                        <span>{exercise.exercise_name || 'Unknown'}</span>
                        <span>{exercise.weight ?? '-'} {userSettings.unit === 'imperial' ? 'lb' : 'kg'}</span>
                      </div>
                      {totalSets > 0 && (
                         <div className="flex justify-between items-center text-xs text-muted-foreground mt-0.5">
                            <span>Reps: {setDetails}</span>
                            <span>Target: {targetRepsDisplay}</span>
                         </div>
                      )}
                       {totalSets === 0 && <p className="text-xs text-muted-foreground mt-0.5">{setDetails}</p>}
                    </div>
                  );
                })}
              </div>
              {workout.notes && (
                <div className="mt-3 pt-2 border-t border-dashed"><p className="text-xs text-muted-foreground italic">Notes: "{workout.notes}"</p></div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
   };

  // Render the Active Workout Tab Content
  const renderActiveWorkout = () => {
    if (loading) return <p className="text-center text-muted-foreground py-8">Loading workout...</p>;
    if (!activeWorkout) {
        return (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Dumbbell className="h-16 w-16 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-semibold">No Active Workout</h3>
            <p className="text-center text-muted-foreground max-w-md">Ready to train? Start a new session from the History tab or button below.</p>
            <div className="flex gap-2">
              <Button onClick={() => setShowStartWorkoutDialog(true)} disabled={needsInitialSetup}>
                <Play className="h-4 w-4 mr-2" /> Start Workout
              </Button>
              <Button variant="outline" onClick={() => setActiveTab('settings')}> {/* Go to settings */}
                <Settings className="h-4 w-4 mr-2" /> Settings
              </Button>
            </div>
             {needsInitialSetup && <p className="text-xs text-destructive mt-2">Please set starting weights first (Settings tab).</p>}
          </div>
        );
      }

    // Active workout exists, render details
    return (
      <div className="space-y-4">
        {/* Header: Exit, Title, Finish */}
        <div className="flex items-center justify-between mb-4 gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowExitDialog(true)} className="text-destructive border-destructive hover:bg-destructive/10">
            <ArrowLeft className="h-4 w-4 mr-1" /> Exit
          </Button>
          <h2 className="text-xl font-bold text-center truncate flex-1 mx-2">{activeWorkout.name}</h2>
          <Button onClick={handleFinishWorkout} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white whitespace-nowrap">
            {loading ? 'Saving...' : <> <CheckCircle2 className="h-4 w-4 mr-1 sm:mr-2" /> Finish </>}
          </Button>
        </div>

        {/* Tabs: Working Sets / Warmup Sets */}
        <Tabs value={showWarmup ? 'warmup' : 'workout'} onValueChange={value => setShowWarmup(value === 'warmup')}>
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="workout">Working Sets</TabsTrigger>
            <TabsTrigger value="warmup">Warmup Sets</TabsTrigger>
          </TabsList>

          {/* Working Sets Content */}
          <TabsContent value="workout" className="space-y-4">
            {activeWorkout.exercises.map((exercise, exerciseIndex) => (
              <Card key={`${exercise.name}-${exerciseIndex}`}>
                <CardHeader className="pb-2">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                     <CardTitle className="text-lg flex-1">{exercise.name}</CardTitle>
                     <div className="text-sm sm:text-right w-full sm:w-auto">
                         <div className="font-medium text-primary flex items-center justify-end sm:justify-start">
                             {exercise.sets}×{exercise.repType === 'static' ? exercise.reps : `${exercise.repMin}-${exercise.repMax}`} @
                             <div className="flex items-center ml-1">
                                 {exercise.weight}{userSettings.unit === 'imperial' ? 'lb' : 'kg'}
                                 <Button variant="ghost" size="icon" className="h-6 w-6 ml-0.5 text-muted-foreground hover:text-primary" onClick={() => { setCurrentEditingExercise({ exerciseIndex, name: exercise.name, weight: exercise.weight }); setShowWeightEditDialog(true); }} title="Edit working weight">
                                     <Edit className="h-3.5 w-3.5" /> <span className="sr-only">Edit Weight</span>
                                 </Button>
                             </div>
                         </div>
                         <div className="text-xs text-muted-foreground mt-0.5">{exercise.plateConfig}</div>
                     </div>
                 </div>
                </CardHeader>
                <CardContent>
                   {/* Rep Bubbles */}
                   <div className="flex gap-2 md:gap-3 mb-4 overflow-x-auto pb-2 -mx-1 px-1">
                      {[...Array(exercise.sets)].map((_, setIndex) => {
                         const completedReps = exercise.completedSets[setIndex] ?? 0;
                         const targetReps = parseInt(exercise.repType === 'static' ? exercise.reps : exercise.repMax, 10);
                         const minReps = parseInt(exercise.repType === 'dynamic' ? exercise.repMin : targetReps, 10);
                         let circleClass = "flex-shrink-0 flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full border-2 font-semibold text-base md:text-lg transition-all duration-150 ease-in-out ";
                         let titleText = `Set ${setIndex + 1}: Tap to ${completedReps === 0 ? 'log reps' : 'decrease'}`;

                          if (completedReps === 0) circleClass += "bg-background border-muted text-muted-foreground";
                          else if (completedReps >= targetReps) circleClass += "bg-green-500 border-green-600 text-white shadow-md";
                          else if (completedReps >= minReps) circleClass += "bg-yellow-400 border-yellow-500 text-yellow-900 shadow";
                          else { circleClass += "bg-red-400 border-red-500 text-red-900"; titleText = `Set ${setIndex + 1}: Failed (${completedReps}/${minReps} reps). Tap to decrease.`; }

                         return <button key={`set-${exerciseIndex}-${setIndex}`} className={circleClass} onClick={() => handleRepTap(exerciseIndex, setIndex)} title={titleText}>{completedReps === 0 ? '' : completedReps}</button>;
                      })}
                   </div>
                   {/* Rest Timer */}
                   <EnhancedRestTimer exerciseId={`${exercise.name}-${exerciseIndex}`} onTimerComplete={handleTimerComplete} defaultTime={exercise.restTime || userSettings.restTimes?.default || 90} />
                </CardContent>
              </Card>
            ))}
             {/* Workout Notes */}
            <Card>
              <CardHeader><CardTitle className="text-lg">Workout Notes</CardTitle></CardHeader>
              <CardContent><Textarea placeholder="Add notes (e.g., form cues, energy levels)..." value={activeWorkout.notes} onChange={(e) => setActiveWorkout(prev => ({ ...prev, notes: e.target.value }))} rows={3} /></CardContent>
            </Card>
          </TabsContent>

          {/* Warmup Sets Content */}
          <TabsContent value="warmup" className="space-y-4">
            {activeWorkout.exercises.map((exercise, exerciseIndex) => (
              <Card key={`warmup-${exercise.name}-${exerciseIndex}`}>
                <CardHeader className="pb-2"><CardTitle className="text-lg">{exercise.name} Warmup</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {exercise.warmupSets && exercise.warmupSets.length > 0 ? (
                      exercise.warmupSets.map((warmup, idx) => (
                        <div key={`warmup-${idx}`} className="flex justify-between items-center p-3 border rounded-lg bg-muted/50">
                          <div>
                            <div className="font-medium">{warmup.reps} reps × {warmup.weight}{userSettings.unit === 'imperial' ? 'lb' : 'kg'}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">{warmup.notes}</div>
                          </div>
                          <div className="text-right text-xs text-muted-foreground">{calculatePlates(warmup.weight, userSettings.barWeight, userSettings.plates)}</div>
                        </div>
                      ))
                    ) : ( <p className="text-center py-4 text-muted-foreground text-sm">No warmup sets calculated (working weight may be low).</p> )}
                    <Separator className="my-3" />
                    {/* Display Working Set Info */}
                     <div className="flex justify-between items-center p-3 border border-primary/50 bg-primary/10 rounded-lg">
                       <div>
                         <div className="font-medium text-primary text-sm">Working: {exercise.sets}×{exercise.repType === 'static' ? exercise.reps : `${exercise.repMin}-${exercise.repMax}`} @ {exercise.weight}{userSettings.unit === 'imperial' ? 'lb' : 'kg'}</div>
                       </div>
                       <div className="text-right text-xs text-primary/80">{exercise.plateConfig}</div>
                     </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    );
   };

  // Render the Settings Tab Content
  const renderSettings = () => {
    if (loading) return <p className="text-center text-muted-foreground py-8">Loading settings...</p>;

    // Derive exercises from the currently active template (loaded in main useEffect)
     let activeTemplateExercises = [];
     const activeTemplateKey = userSettings.activeTemplate || '';
     if (activeTemplateKey) {
         let templateDefinition;
         if (activeTemplateKey.startsWith('custom_')) {
             const templateId = activeTemplateKey.replace('custom_', '');
             templateDefinition = templates.find(t => t.id === templateId);
         } else {
             templateDefinition = WORKOUT_TEMPLATES[activeTemplateKey];
         }
          if (templateDefinition?.workouts) {
              const exerciseSet = new Set();
              Object.values(templateDefinition.workouts).forEach(w => (w.exercises || []).forEach(e => e.name && exerciseSet.add(JSON.stringify(e))));
              activeTemplateExercises = Array.from(exerciseSet).map(exString => {
                  try {
                      const exObj = JSON.parse(exString);
                       const defaultIncrement = exObj.increment ?? (exObj.name?.toLowerCase().includes('deadlift') ? 10 : 5);
                      return { name: exObj.name, defaultIncrement };
                  } catch { return null; }
              }).filter(Boolean);
          }
     }

    const handleSaveSettings = async () => {
         if (!user?.id) return;
         setLoading(true);
         try {
             // Save workout settings
             const { error: settingsError } = await supabase.from('user_workout_settings').upsert({
                 id: user.id, unit: userSettings.unit, bar_weight: userSettings.barWeight,
                 plates: userSettings.plates, rest_times: userSettings.restTimes,
                 increment_settings: userSettings.incrementSettings,
                 active_template: userSettings.activeTemplate,
                 last_workout_type: userSettings.lastWorkoutType,
                 updated_at: new Date().toISOString()
             }, { onConflict: 'id' });
             if (settingsError) throw settingsError;

             // Optionally save general preferences if they were modified here (though less common in workout settings)
             // Example: If you added age/gender fields here
             // const { error: prefsError } = await supabase.from('user_preferences').upsert({ ... }, { onConflict: 'id' });
             // if (prefsError) throw prefsError;

             toast({ title: 'Settings Saved', description: 'Workout preferences updated.' });
         } catch (e) {
             console.error('Error saving settings:', e);
             toast({ title: 'Error', description: `Save failed: ${e.message}`, variant: 'destructive' });
         } finally {
             setLoading(false);
         }
     };

    return (
      <Card>
        <CardHeader><CardTitle>Workout Settings</CardTitle><CardDescription>Customize your workout tracking experience</CardDescription></CardHeader>
        <CardContent className="space-y-6">
          {/* Unit System */}
          <div className="space-y-2">
            <Label>Unit System</Label>
            <RadioGroup value={userSettings.unit} onValueChange={(value) => setUserSettings(prev => ({ ...prev, unit: value, plates: value==='metric'?DEFAULT_PLATES_KG:DEFAULT_PLATES_LBS, barWeight: value==='metric'?DEFAULT_BAR_WEIGHT.metric:DEFAULT_BAR_WEIGHT.imperial }))} className="flex space-x-4 pt-1">
              <div className="flex items-center space-x-2"><RadioGroupItem value="imperial" id="s-imperial" /><Label htmlFor="s-imperial">Imperial (lbs)</Label></div>
              <div className="flex items-center space-x-2"><RadioGroupItem value="metric" id="s-metric" /><Label htmlFor="s-metric">Metric (kg)</Label></div>
            </RadioGroup>
          </div>
          {/* Bar Weight */}
          <div className="space-y-2">
            <Label htmlFor="barWeight">Bar Weight ({userSettings.unit === 'imperial' ? 'lbs' : 'kg'})</Label>
            <Input id="barWeight" type="number" value={userSettings.barWeight} onChange={(e) => setUserSettings(prev => ({ ...prev, barWeight: parseFloat(e.target.value) || (prev.unit === 'metric' ? DEFAULT_BAR_WEIGHT.metric : DEFAULT_BAR_WEIGHT.imperial) }))} step="0.1" className="w-24"/>
          </div>
          {/* Plates */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex justify-between items-center"><Label>Available Plates</Label><Button variant="outline" size="sm" onClick={() => setUserSettings(prev => ({ ...prev, plates: prev.unit === 'metric' ? DEFAULT_PLATES_KG : DEFAULT_PLATES_LBS }))}>Reset Defaults</Button></div>
            <ScrollArea className="h-[200px] border rounded-md p-3"><div className="space-y-3">
              {(userSettings.plates || []).map((plate, index) => (
                <div key={`plate-${index}`} className="flex items-center gap-2">
                  <Input aria-label={`Plate ${index+1} weight`} type="number" value={plate.weight} onChange={(e)=>{const nP=[...userSettings.plates]; nP[index]={...nP[index], weight:parseFloat(e.target.value)||0}; setUserSettings(p=>({...p, plates:nP.sort((a,b)=>b.weight-a.weight)}))}} className="w-20 h-8" step="0.1"/>
                  <span className="text-sm text-muted-foreground">{userSettings.unit==='metric'?'kg':'lbs'}</span><span className="mx-1 text-muted-foreground">×</span>
                  <Input aria-label={`Plate ${index+1} count`} type="number" value={plate.count} onChange={(e)=>{const nP=[...userSettings.plates]; nP[index]={...nP[index], count:Math.max(0, parseInt(e.target.value)||0)}; setUserSettings(p=>({...p, plates:nP}))}} className="w-16 h-8" min="0" step="2"/>
                  {/*<span className="text-sm text-muted-foreground">count</span>*/}
                  <Button variant="ghost" size="icon" onClick={()=>{const nP=userSettings.plates.filter((_,i)=>i!==index); setUserSettings(p=>({...p, plates:nP}))}} className="ml-auto h-7 w-7 text-destructive hover:bg-destructive/10"><Minus className="h-4 w-4"/><span className="sr-only">Remove Plate</span></Button>
                </div>
              ))}
            </div></ScrollArea>
            <Button variant="outline" onClick={()=>{const nW=userSettings.unit==='metric'? 0.5 : 1.25; const nP=[...(userSettings.plates||[]),{weight:nW,count:2}]; setUserSettings(p=>({...p, plates:nP.sort((a,b)=>b.weight-a.weight)}))}} className="w-full"><Plus className="h-4 w-4 mr-2"/>Add Plate Type</Button>
          </div>
          {/* Starting Weights */}
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5"><Label>Starting Weights</Label><p className="text-sm text-muted-foreground">Adjust starting weights for the active program.</p></div>
                <Button variant="outline" onClick={() => setShowWeightsDialog(true)} disabled={!userSettings.activeTemplate}><Edit className="h-4 w-4 mr-2"/>Adjust</Button>
            </div>
          </div>
          {/* Rest Times */}
          <div className="pt-4 border-t">
            <h4 className="text-base font-medium mb-3">Rest Times</h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5"><Label htmlFor="defaultRest">Default</Label><p className="text-xs text-muted-foreground">Used if no specific time set.</p></div>
                  <div className="flex items-center gap-2"><Input id="defaultRest" type="number" value={userSettings.restTimes?.default ?? 90} onChange={(e)=>{const nV=Math.max(15,parseInt(e.target.value)||90); setUserSettings(p=>({...p,restTimes:{...p.restTimes, default:nV}}))}} className="w-20 h-8" min="15" step="15"/><span className="text-sm text-muted-foreground">sec</span></div>
              </div>
               <div className="space-y-2">
                   <Label>Exercise Specific Overrides</Label>
                   <p className="text-xs text-muted-foreground">For <span className="font-medium">{ templates.find(t => `custom_${t.id}` === activeTemplateKey)?.name || WORKOUT_TEMPLATES[activeTemplateKey]?.name || 'Active Program' }</span>.</p>
                   <ScrollArea className="h-[150px] border rounded-md p-2 mt-1"><div className="space-y-1.5 pr-2">
                     {activeTemplateExercises.length > 0 ? (
                       activeTemplateExercises.sort((a,b)=>a.name.localeCompare(b.name)).map(ex => (
                         <div key={`rest-${ex.name}`} className="flex justify-between items-center text-sm gap-2">
                             <span className="truncate flex-1" title={ex.name}>{ex.name}</span>
                             <div className="flex items-center gap-1">
                               <Input aria-label={`${ex.name} rest time`} type="number" value={userSettings.restTimes?.exercises?.[ex.name] ?? ''} onChange={(e)=>{const v=e.target.value; const nV=v?Math.max(15,parseInt(v)):undefined; setUserSettings(p=>{const nERT={...(p.restTimes?.exercises || {})}; if(nV===undefined)delete nERT[ex.name]; else nERT[ex.name]=nV; return {...p, restTimes:{...(p.restTimes || {}), exercises:nERT}};})}} placeholder={String(userSettings.restTimes?.default || 90)} className="w-16 h-8" min="15" step="15"/>
                               <span className="text-xs text-muted-foreground">s</span>
                             </div>
                         </div>
                       ))
                     ) : ( <p className="text-center text-xs text-muted-foreground py-4">{activeTemplateKey ? 'No exercises found.' : 'Select program.'}</p> )}
                   </div></ScrollArea>
               </div>
            </div>
          </div>
           {/* Weight Progression */}
           <div className="pt-4 border-t">
             <h4 className="text-base font-medium mb-3">Weight Progression Increments</h4>
             <div className="space-y-2">
                 <p className="text-xs text-muted-foreground mb-2">Override default increase per exercise ({userSettings.unit === 'imperial' ? 'lbs' : 'kg'}). Leave blank for default.</p>
                 <ScrollArea className="h-[150px] border rounded-md p-2"><div className="space-y-1.5 pr-2">
                     {activeTemplateExercises.length > 0 ? (
                         activeTemplateExercises.sort((a, b) => a.name.localeCompare(b.name)).map(ex => (
                             <div key={`inc-${ex.name}`} className="flex justify-between items-center text-sm gap-2">
                                 <span className="truncate flex-1" title={ex.name}>{ex.name}</span>
                                 <div className="flex items-center gap-1">
                                     <Input aria-label={`${ex.name} weight increment`} type="number" value={userSettings.incrementSettings?.[ex.name] ?? ''}
                                         onChange={(e) => {
                                             const v = e.target.value; const nV = v === '' ? undefined : parseFloat(v);
                                             setUserSettings(p => { const nI = { ...(p.incrementSettings || {}) }; if (nV === undefined) delete nI[ex.name]; else if (!isNaN(nV) && nV > 0) nI[ex.name] = nV; return { ...p, incrementSettings: nI }; });
                                         }}
                                         placeholder={String(ex.defaultIncrement)} className="w-16 h-8" min="0.1" step={userSettings.unit === 'imperial' ? 0.5 : 0.25}
                                     />
                                 </div>
                             </div>
                         ))
                     ) : ( <p className="text-center text-xs text-muted-foreground py-4">{activeTemplateKey ? 'No exercises found.' : 'Select program.'}</p> )}
                 </div></ScrollArea>
             </div>
           </div>
          {/* Re-run Onboarding */}
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5"><Label>Profile Setup</Label><p className="text-sm text-muted-foreground">Re-run the setup wizard for basic profile info.</p></div>
              <Button variant="outline" onClick={resetOnboardingStatus} disabled={loading}>{loading ? "..." : "Re-run Setup"}</Button>
            </div>
          </div>
          {/* Save All Button */}
          <Button onClick={handleSaveSettings} disabled={loading} className="w-full mt-4">{loading ? 'Saving...' : 'Save Workout Settings'}</Button>
        </CardContent>
      </Card>
    );
  };

  // Callback after OnboardingWizard completes and saves data
  const handleOnboardingComplete = (savedData) => {
    console.log("WorkoutPage: Onboarding complete callback received:", savedData);

    // Update local userSettings state with workout-related info from onboarding
    setUserSettings(prev => ({
        ...prev,
        unit: savedData.unit || prev.unit,
        activeTemplate: savedData.active_template || prev.activeTemplate,
        barWeight: savedData.bar_weight || (savedData.unit === 'metric' ? 20 : 45),
        plates: savedData.plates || (savedData.unit === 'metric' ? DEFAULT_PLATES_KG : DEFAULT_PLATES_LBS),
         // Update rest times/increments if they were set/saved in onboarding (optional)
         restTimes: savedData.rest_times || prev.restTimes,
         incrementSettings: savedData.increment_settings || prev.incrementSettings,
    }));

    // Update general user preferences state
    setUserPreferencesData(prev => ({
        ...(prev || {}), // Keep existing prefs
        ...savedData, // Merge all saved data from onboarding
        onboarding_complete: true // Ensure flag is true
    }));

    // Reset flags and potentially fetched history
    setNeedsInitialSetup(false); // Onboarding implies basic setup is done
    setRecentHistory(null); // Clear history used for prefill
    setShowOnboarding(false); // Ensure dialog is closed state-wise

    toast({ title: "Profile Setup Complete!", description: "Your settings have been updated." });

     // Optional: Trigger a re-evaluation or specific action based on new settings
     // e.g., if activeTemplate changed, check needsInitialSetup again
     // Note: The main data loading useEffect doesn't run again, so manual checks might be needed
     // or trigger a lightweight refresh function if complex dependencies exist.
     // For now, setting needsInitialSetup=false is likely sufficient.
  };


  // --- MAIN RETURN JSX ---
  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        {/* Tabs List */}
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="workout">Workout</TabsTrigger>
          <TabsTrigger value="progress">Progress</TabsTrigger>
          <TabsTrigger value="program">Program</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <div className="flex justify-between items-center gap-2">
            <h2 className="text-2xl font-bold">Workout History</h2>
            <Button onClick={() => setShowStartWorkoutDialog(true)} disabled={loading || needsInitialSetup}>
              <Play className="h-4 w-4 mr-1 sm:mr-2" /> Start Workout
            </Button>
          </div>
          {renderWorkoutHistory()}
        </TabsContent>

        {/* Workout Tab */}
        <TabsContent value="workout">{renderActiveWorkout()}</TabsContent>

        {/* Progress Tab */}
        <TabsContent value="progress">
           {loading ? ( <p className="text-center text-muted-foreground py-8">Loading progress...</p> ) : (
               <div className="space-y-6">
                   {user?.id ? (
                       <>
                           <ExerciseProgressChart userId={user.id} unitSystem={userSettings.unit} />
                           <PersonalRecords userId={user.id} unitSystem={userSettings.unit} />
                       </>
                   ) : ( <p className="text-center text-muted-foreground py-8">Log in to view progress.</p> )}
               </div>
           )}
        </TabsContent>

          {/* Program Tab */}
          <TabsContent value="program">
            {loading ? ( <p className="text-center text-muted-foreground py-8">Loading programs...</p> ) : (
              <div className="space-y-6">
                {/* Active Program Selector Card */}
                <Card>
                  <CardHeader><CardTitle>Active Workout Program</CardTitle><CardDescription>Select your current training plan.</CardDescription></CardHeader>
                  <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="active-program-select">Selected Program</Label>
                        <Select value={userSettings.activeTemplate || ''} onValueChange={(value) => value && saveTemplatePreference(value)} disabled={!user?.id}>
                          <SelectTrigger id="active-program-select"><SelectValue placeholder="Select a program..." /></SelectTrigger>
                          <SelectContent>
                            <SelectGroup><SelectLabel>Built-in Programs</SelectLabel>{Object.entries(WORKOUT_TEMPLATES).map(([k, t])=>(<SelectItem key={k} value={k}>{t.name}</SelectItem>))}</SelectGroup>
                            {templates?.length > 0 && (<><SelectSeparator /><SelectGroup><SelectLabel>Your Custom Programs</SelectLabel>{templates.map((t)=>(<SelectItem key={t.id} value={`custom_${t.id}`}>{t.name}</SelectItem>))}</SelectGroup></>)}
                            {!templates?.length && (<><SelectSeparator /><div className="px-2 py-1.5 text-xs text-muted-foreground text-center">No custom programs yet.</div></>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button variant="outline" className="w-full" onClick={() => handlePreviewProgram(userSettings.activeTemplate)} disabled={!userSettings.activeTemplate}><Eye className="h-4 w-4 mr-2" />Preview Active Program</Button>
                  </CardContent>
                </Card>
                {/* Managers */}
                {user?.id ? (
                    <>
                      <WorkoutTemplateManager userId={user.id} userSettings={userSettings} onTemplateDeleted={handleTemplateDeleted} onTemplateAdded={handleTemplateAdded} onTemplateActivated={saveTemplatePreference} onPreviewTemplate={(t) => { setPreviewProgramData(t); setShowProgramPreview(true); }}/>
                      <CustomExercisesManager userId={user.id} />
                    </>
                ) : ( <p className="text-center text-muted-foreground py-8">Log in to manage programs.</p> )}
              </div>
            )}
          </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">{renderSettings()}</TabsContent>
      </Tabs>

      {/* ==================== DIALOGS ==================== */}

      {/* Start Workout Dialog */}
      {!loading && showStartWorkoutDialog && user?.id && (
        <Dialog open={showStartWorkoutDialog} onOpenChange={setShowStartWorkoutDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>Start Workout</DialogTitle><DialogDescription>Choose which workout day to begin.</DialogDescription></DialogHeader>
            <div className="py-4 space-y-4">
               {/* Program selection (optional, could just use active) */}
               <div>
                   <Label className="mb-1 block text-sm font-medium">Using Program:</Label>
                   <p className="font-semibold">{ templates.find(t => `custom_${t.id}` === userSettings.activeTemplate)?.name || WORKOUT_TEMPLATES[userSettings.activeTemplate]?.name || 'Unknown Program' }</p>
                   <Button variant="link" size="sm" className="p-0 h-auto text-xs" onClick={() => { setShowStartWorkoutDialog(false); setActiveTab('program'); }}>Change Program</Button>
               </div>
               <Separator/>
               <div>
                   <Label className="mb-2 block text-sm font-medium">Select Workout Day:</Label>
                   <div className="grid gap-3">
                     {(() => {
                         const activeKey = userSettings.activeTemplate; let workoutOptions = {};
                         if (activeKey?.startsWith('custom_')) { const tId = activeKey.replace('custom_', ''); const customT = templates.find(t => t.id === tId); workoutOptions = customT?.workouts || {}; }
                         else if (WORKOUT_TEMPLATES[activeKey]) { workoutOptions = WORKOUT_TEMPLATES[activeKey].workouts || {}; }
                         if (Object.keys(workoutOptions).length === 0) { return <p className="text-sm text-center text-muted-foreground py-4">No workout days defined.</p>; }
                         // Sort workout keys if possible (e.g., 'a', 'b' or 'day1', 'day2')
                         const sortedKeys = Object.keys(workoutOptions).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                         return sortedKeys.map(key => {
                             const workout = workoutOptions[key];
                             return (
                                 <Button key={key} variant="outline" onClick={() => handleStartWorkout(key)} className="flex justify-between items-center p-3 h-auto text-left">
                                     <div className="flex flex-col items-start"><span className="font-medium">{workout.name || `Workout ${key}`}</span><span className="text-xs text-muted-foreground mt-0.5">{(workout.exercises?.length || 0)} exercises</span></div>
                                     <ChevronRight className="h-4 w-4 ml-2 flex-shrink-0" />
                                 </Button>
                             );
                         });
                     })()}
                   </div>
               </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Weight During Workout Dialog */}
      <Dialog open={showWeightEditDialog} onOpenChange={setShowWeightEditDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle>Adjust Working Weight</DialogTitle><DialogDescription>Update weight for {currentEditingExercise?.name}</DialogDescription></DialogHeader>
          {currentEditingExercise && (
            <div className="grid gap-4 py-4">
              <div className="flex items-center gap-2">
                <Label htmlFor="edit-weight" className="w-16">Weight</Label>
                <Input id="edit-weight" type="number" step={userSettings.unit === 'imperial' ? 2.5 : 1} min="0" value={currentEditingExercise.weight} onChange={(e) => setCurrentEditingExercise(prev => ({ ...prev, weight: parseFloat(e.target.value) || 0 }))} className="flex-1"/>
                <span className="text-sm text-muted-foreground">{userSettings.unit === 'imperial' ? 'lbs' : 'kg'}</span>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowWeightEditDialog(false)}>Cancel</Button><Button onClick={() => { if (!currentEditingExercise) return; setActiveWorkout(prev => { if (!prev) return null; const newExercises = [...prev.exercises]; const exIndex = currentEditingExercise.exerciseIndex; const updatedWeight = currentEditingExercise.weight; newExercises[exIndex] = { ...newExercises[exIndex], weight: updatedWeight, warmupSets: calculateWarmupSets(updatedWeight, newExercises[exIndex], userSettings.unit), plateConfig: calculatePlates(updatedWeight, userSettings.barWeight, userSettings.plates) }; return { ...prev, exercises: newExercises }; }); setShowWeightEditDialog(false); setCurrentEditingExercise(null); }}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exit Workout Confirmation */}
      {showExitDialog && (
          <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
            <DialogContent><DialogHeader><DialogTitle>Exit Workout?</DialogTitle><DialogDescription>Progress for this session will be lost.</DialogDescription></DialogHeader><DialogFooter><Button variant="outline" onClick={() => setShowExitDialog(false)}>Cancel</Button><Button variant="destructive" onClick={() => { setActiveWorkout(null); setShowExitDialog(false); setActiveTab('history'); }}>Exit Without Saving</Button></DialogFooter></DialogContent>
          </Dialog>
      )}

      {/* Delete Workout Confirmation */}
      {showDeleteDialog && (
          <DeleteWorkoutDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog} workoutToDelete={workoutToDelete} onConfirmDelete={handleDeleteWorkout} isDeleting={isDeleting}/>
      )}

      {/* Initial Weights Setup Dialog */}
      {!loading && showWeightsDialog && user?.id && (
          <InitialWeightsDialog open={showWeightsDialog} onOpenChange={setShowWeightsDialog} userId={user.id}
            exercises={(() => {
                const activeKey = userSettings.activeTemplate || ''; let templateDef;
                if (activeKey.startsWith('custom_')) templateDef = templates.find(t => t.id === activeKey.replace('custom_', ''));
                else templateDef = WORKOUT_TEMPLATES[activeKey];
                if (!templateDef?.workouts) return [];
                const exNames = new Set();
                Object.values(templateDef.workouts).forEach(w=>(w.exercises||[]).forEach(e=>e.name && exNames.add(e.name)));
                return Array.from(exNames).map(name=>({name})).sort((a,b)=>a.name.localeCompare(b.name));
            })()}
            currentWeights={Object.fromEntries(Object.entries(exerciseProgress).map(([ex,data])=>[ex,data?.currentWeight??'']))}
            unitSystem={userSettings.unit} onWeightsUpdated={handleWeightsUpdated}
          />
      )}

       {/* Onboarding Wizard */}
       {/* Construct userPreferences prop ONLY when rendering */}
       {(() => {
           // This IIFE runs on every render, creating the latest prefs object
           const wizardPrefs = {
               // Base defaults
               gender: 'male', unit: 'imperial', age: 30, activityLevel: 1.375,
               height_feet: 5, height_inches: 10, height_cm: 178,
               weight: '', body_fat: '',
               // Merge workout settings
               ...userSettings,
               // Merge general preferences if loaded
               ...(userPreferencesData || {}),
               // *Override* with most recent history if available (for specific fields)
               ...(recentHistory ? {
                   weight: recentHistory.weight ?? (userPreferencesData?.weight || ''), // Prioritize history, then prefs, then empty
                   body_fat: recentHistory.body_fat ?? (userPreferencesData?.body_fat || ''),
                   activity_level: recentHistory.activity_level ?? (userPreferencesData?.activity_level || 1.375),
                   age: recentHistory.age ?? (userPreferencesData?.age || 30),
                    // Height from history might be only in CM, be careful merging
                    // Prefer height from userPreferencesData if available, as it might have ft/in
                   height_cm: recentHistory.height_cm ?? userPreferencesData?.height_cm ?? 178,
                   height_feet: userPreferencesData?.height_feet ?? (recentHistory.height_cm ? Math.floor(recentHistory.height_cm / 30.48) : 5),
                   height_inches: userPreferencesData?.height_inches ?? (recentHistory.height_cm ? Math.round((recentHistory.height_cm % 30.48) / 2.54) : 10),

               } : {})
           };
           // Clean up potential null/undefined values that should be empty strings for inputs
           wizardPrefs.weight = wizardPrefs.weight ?? '';
           wizardPrefs.bodyFat = wizardPrefs.bodyFat ?? '';

           // Log just before rendering the wizard
           if (showOnboarding) {
                console.log("WorkoutPage: Rendering OnboardingWizard with props:", wizardPrefs);
           }

           return (
               <OnboardingWizard
                   open={showOnboarding}
                   onOpenChange={setShowOnboarding}
                   userId={user?.id}
                   userPreferences={wizardPrefs} // Pass the dynamically created object
                   setUserSettings={setUserSettings}
                   onComplete={handleOnboardingComplete}
               />
           );
       })()}


      {/* Workout Program Preview Modal */}
      <WorkoutProgramPreview open={showProgramPreview} onOpenChange={setShowProgramPreview} programData={previewProgramData} unitSystem={userSettings.unit}
        onActivate={previewProgramData && userSettings.activeTemplate !== (previewProgramData.id ? `custom_${previewProgramData.id}` : previewProgramData.templateKey) ? () => {
            const templateKey = previewProgramData.id ? `custom_${previewProgramData.id}` : previewProgramData.templateKey;
            if (templateKey) { saveTemplatePreference(templateKey); setShowProgramPreview(false); toast({ title: "Program Activated", description: `${previewProgramData.name} is now active.` }); }
            else { toast({ title: "Error", description: "Could not activate program.", variant: "destructive"}); }
          } : undefined }
      />

    </div> // End container div
  );
};

export default WorkoutPage;